import React from "react"
import { twMerge } from "tailwind-merge"

import { getFlexAlignItemsFromCtaAlign } from "../utils/alignmentUtils"
import { getSectionColorScheme } from "../utils/colorModeUtils"
import { getContrastingTextColor as getContrastingTextColorUtil } from "../utils/colorUtils"
import { formatFontFamily, formatHeadingFontFamily } from "../utils/fontUtils"
import { getHeroImageSrcSet } from "../utils/heroImageUtils"
import { getReviewDisplayData, renderStarRating } from "../utils/reviewUtils"
import { applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import {
  buildPublicPageUrl,
  buildServiceUrl,
  buildBookingUrl,
  buildDefaultPageUrl,
  buildEventTypeUrl,
  resolveBookableEventTypeSlugForCta
} from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import HeaderSectionDesktop from "./HeaderSectionDesktop"
import ShapeBorder, { hasShapeBorder, getShapeTypeFromLayout, getBaseLayoutFromShapeLayout } from "./ShapeBorder"
import { ScrollReveal, ScrollRevealItem } from "./shared/ScrollReveal"

// Non-full-image layouts that support color mode
const NON_FULL_IMAGE_LAYOUTS = ["image_left", "image_right", "half_image_left", "half_image_right"]

// Helper function to determine text color based on background
const getContrastingTextColor = (
  layout,
  hasBackgroundImage,
  theme,
  isInWhiteContainer = false,
  colorMode = "white"
) => {
  // If content is in a white container (like scattered_images with multiple images), use dark text
  if (isInWhiteContainer) {
    return theme?.colors?.textDark || "#000000"
  }
  // For non-full-image layouts with dark color mode, use light text
  if (NON_FULL_IMAGE_LAYOUTS.includes(layout) && colorMode === "dark") {
    return theme?.colors?.textLight || "#FFFFFF"
  }
  // If there's a background image or it's a full image layout, use light text
  if (hasBackgroundImage || layout === "full_image" || layout.includes("image")) {
    return theme?.colors?.textLight || "#FFFFFF"
  }
  // Otherwise use dark text for light backgrounds
  return theme?.colors?.textDark || "#55433F"
}

const HeroSectionDesktop = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "full_image"

  // const textAlign = settings.textAlign || "left"
  const overlayOpacity = settings.overlayOpacity || 30
  const showCTA = settings.showCTA !== undefined ? settings.showCTA : true
  const ctaText = settings.ctaText || settings.ctaButtonText || "Learn More"
  const ctaAction = settings.ctaAction || "booking"
  const ctaTarget = settings.ctaTarget || ""

  // Get color scheme for non-full-image layouts
  const colorScheme = getSectionColorScheme(settings, theme)
  const sectionBackgroundColor = NON_FULL_IMAGE_LAYOUTS.includes(layout) ? colorScheme.sectionBackground : "transparent"
  const colorMode = colorScheme.colorMode

  // full_image: use white/solid background when showBackgroundColor is set (e.g. V1 "all white" hero), else theme primary
  const fullImageSectionBackground =
    settings.showBackgroundColor && settings.backgroundColor
      ? settings.backgroundColor
      : theme?.colors?.primary || "#3B82F6"

  // Get review data from practice
  const reviewData = getReviewDisplayData(practice, settings.showReviews)
  const showReviews = settings.showReviews !== undefined ? settings.showReviews : reviewData.showReviews

  const getServiceOrEventTarget = () => {
    if (!ctaTarget) return null

    const rawTarget = String(ctaTarget).trim()
    if (!rawTarget) return null

    if (rawTarget.startsWith("service:")) {
      return { type: "service", value: rawTarget.replace(/^service:/, "") }
    }

    if (rawTarget.startsWith("event:")) {
      return { type: "event", value: rawTarget.replace(/^event:/, "") }
    }

    // Backward compatibility for legacy targets without explicit prefix.
    const legacyEventSlug = resolveBookableEventTypeSlugForCta(rawTarget, practice)
    if (legacyEventSlug) {
      return { type: "event", value: legacyEventSlug }
    }

    return { type: "service", value: rawTarget }
  }

  const handleCTAClick = () => {
    const scrollToLayout = (layoutId) => {
      if (!layoutId) return
      const targetElement = document.querySelector(`[id^=section-${layoutId}-]`)
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth" })
      }
    }
    switch (ctaAction) {
      case "page": {
        if (ctaTarget) {
          window.location.href = buildPublicPageUrl(ctaTarget, practice)
        }
        break
      }
      case "service":
        if (ctaTarget) {
          const target = getServiceOrEventTarget()
          if (target?.type === "event") {
            window.location.href = buildEventTypeUrl(target.value, practice)
          } else if (target?.type === "service") {
            window.location.href = buildServiceUrl(target.value, practice)
          }
        }
        break
      case "booking":
        window.location.href = buildBookingUrl(practice)
        break
      case "contact":
        scrollToLayout("contact")
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        if (ctaTarget) {
          // Sanitize URL: ensure it starts with http:// or https://
          let sanitizedUrl = ctaTarget.trim()
          if (!sanitizedUrl.startsWith("http://") && !sanitizedUrl.startsWith("https://")) {
            sanitizedUrl = `https://${sanitizedUrl}`
          }
          // Additional validation: check if URL is valid
          try {
            new URL(sanitizedUrl)
            window.open(sanitizedUrl, "_blank", "noopener,noreferrer")
          } catch (e) {
            // Invalid URL - silently fail
          }
        }
        break
      default:
        break
    }
  }

  const renderContent = (hasBackgroundImage = false, customLayout = null, isInWhiteContainer = false) => {
    const currentLayout = customLayout || layout
    const textColor = getContrastingTextColor(currentLayout, hasBackgroundImage, theme, isInWhiteContainer, colorMode)
    // Same process as other sections: use color scheme for non-full-image layouts
    const headingColor = NON_FULL_IMAGE_LAYOUTS.includes(currentLayout) ? colorScheme.headingColor : textColor
    const bodyColor = NON_FULL_IMAGE_LAYOUTS.includes(currentLayout) ? colorScheme.bodyColor : textColor

    return (
      <div className="flex w-full flex-col">
        <div className="flex w-full flex-col gap-[16px]">
          {section.headline && (
            <ScrollRevealItem staggerIndex={0} variant="fadeUp" threshold={0}>
              <h1
                style={{
                  fontSize: settings.headlineSize || "56px",
                  fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                  fontWeight: theme?.fonts?.headingWeight || "700",
                  color: settings.headlineColor || headingColor,
                  lineHeight: "1.1",
                  textAlign: settings.headingAlign || settings.textAlign || "center"
                }}>
                {section.headline}
              </h1>
            </ScrollRevealItem>
          )}
          {section.subheadline && (
            <ScrollRevealItem staggerIndex={1} variant="fadeUp" threshold={0}>
              <p
                className="w-full"
                style={{
                  fontSize: settings.subheadlineSize || "20px",
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                  color: settings.subheadlineColor || bodyColor,
                  textAlign: settings.subheadingAlign || settings.textAlign || "center"
                }}>
                {section.subheadline}
              </p>
            </ScrollRevealItem>
          )}
        </div>

        <ScrollRevealItem staggerIndex={2} variant="fadeUp" threshold={0} className="w-full">
          <div
            className="flex w-full flex-col gap-[20px]"
            style={{
              alignItems: getFlexAlignItemsFromCtaAlign(settings.ctaAlign),
              marginTop: settings.ctaTopMargin ? `${settings.ctaTopMargin}px` : "48px"
            }}>
            {/* CTA Button */}
            {showCTA && (
              <button
                type="button"
                aria-label={ctaText || "Call to action"}
                onClick={handleCTAClick}
                className={twMerge(
                  "flex items-center justify-center gap-[12px] px-40 py-[12px] text-[16px] font-normal leading-[24px] transition-all hover:shadow-lg",
                  getButtonClasses(theme?.buttonStyle)
                )}
                style={applyButtonTheme(
                  {
                    backgroundColor: theme?.colors?.primary || "#BD654F",
                    color: getContrastingTextColorUtil(theme?.colors?.primary || "#BD654F", theme),
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                  },
                  theme
                )}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                  e.target.style.color = getContrastingTextColorUtil(theme?.colors?.primaryDark || "#A55A45", theme)
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                  e.target.style.color = getContrastingTextColorUtil(theme?.colors?.primary || "#BD654F", theme)
                }}>
                {ctaText}
              </button>
            )}

            {/* Client Reviews - matching Figma specs */}
            {showReviews && (
              <div className="flex items-center">
                <div className="mr-2 flex h-[20px] items-start">
                  {renderStarRating(reviewData.averageRating, "h-[20px] w-[20px]")}
                </div>
                <a
                  href={buildDefaultPageUrl(practice, "reviews", "review")}
                  className="text-[16px] font-semibold leading-[24px] underline"
                  style={{ color: settings.reviewTextColor || settings.subheadlineColor || bodyColor }}>
                  {reviewData.reviewCount} verified review{reviewData.reviewCount !== 1 ? "s" : ""}
                </a>
              </div>
            )}
          </div>
        </ScrollRevealItem>
      </div>
    )
  }

  // Get the effective layout for rendering (handle shape border layouts)
  const effectiveLayout = getBaseLayoutFromShapeLayout(layout)
  // Shape border can come from settings.shapeBorder (new way) or be encoded in layout name (legacy)
  const shapeType =
    settings.shapeBorder && settings.shapeBorder !== "none" ? settings.shapeBorder : getShapeTypeFromLayout(layout)
  const showShapeBorder = (settings.shapeBorder && settings.shapeBorder !== "none") || hasShapeBorder(layout)

  // Layout-specific rendering based on Figma design
  switch (effectiveLayout) {
    case "image_left":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{
            backgroundColor: sectionBackgroundColor
          }}>
          <ScrollReveal variant="fadeUp" threshold={0} className="mx-auto w-full px-[140px] py-[108px]">
            <div className="flex h-[600px] items-center">
              {section.imageUrl || (settings.images && settings.images.length > 0) ? (
                <div className="relative h-[600px] w-1/2 overflow-hidden">
                  <img
                    src={section.imageUrl || settings.images[0].url}
                    srcSet={getHeroImageSrcSet(section.imageUrl || settings.images[0].url)}
                    alt=""
                    className="size-full object-cover object-center"
                    loading="eager"
                    fetchpriority="high"
                  />
                </div>
              ) : (
                <div className="flex h-[600px] w-1/2 items-center justify-center bg-gray-200">
                  <span className="text-gray-500">No image selected</span>
                </div>
              )}
              <div className="flex w-1/2 flex-col items-start justify-center gap-[48px] px-[52px]">
                {renderContent(false)}
              </div>
            </div>
          </ScrollReveal>
        </section>
      )

    case "image_right":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{
            backgroundColor: sectionBackgroundColor
          }}>
          <ScrollReveal variant="fadeUp" threshold={0} className="mx-auto w-full px-[140px] py-[108px]">
            <div className="flex h-[600px] items-center">
              <div className="flex w-1/2 flex-col items-start justify-center gap-[48px] px-[52px]">
                {renderContent(false)}
              </div>
              {section.imageUrl || (settings.images && settings.images.length > 0) ? (
                <div className="relative h-[600px] w-1/2 overflow-hidden">
                  <img
                    src={section.imageUrl || settings.images[0].url}
                    srcSet={getHeroImageSrcSet(section.imageUrl || settings.images[0].url)}
                    alt=""
                    className="size-full object-cover object-center"
                    loading="eager"
                    fetchpriority="high"
                  />
                </div>
              ) : (
                <div className="flex h-[600px] w-1/2 items-center justify-center bg-gray-200">
                  <span className="text-gray-500">No image selected</span>
                </div>
              )}
            </div>
          </ScrollReveal>
        </section>
      )

    case "half_image_left": {
      return (
        <section
          className={twMerge("relative flex h-[728px] items-center", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <ScrollReveal variant="fadeUp" threshold={0} className="mx-auto w-full py-[108px]">
            <div className="flex h-[728px] items-center">
              {/* Image on the left - half width */}
              {section.imageUrl || (settings.images && settings.images.length > 0) ? (
                <div className="relative h-[728px] w-1/2 overflow-hidden">
                  <img
                    src={section.imageUrl || settings.images[0].url}
                    srcSet={getHeroImageSrcSet(section.imageUrl || settings.images[0].url)}
                    alt=""
                    className="size-full object-cover object-center"
                    loading="eager"
                  />
                </div>
              ) : (
                <div className="flex h-[728px] w-1/2 items-center justify-center bg-gray-200">
                  <span className="text-gray-500">No image selected</span>
                </div>
              )}
              {/* Content on the right - half width with white background */}
              <div className="flex h-[728px] w-1/2 shrink-0 flex-col items-start justify-center bg-white px-[80px] py-[137px]">
                {renderContent(false, null, true)}
              </div>
            </div>
          </ScrollReveal>
        </section>
      )
    }

    case "half_image_right": {
      return (
        <section
          className={twMerge("relative flex h-[728px] items-center", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <ScrollReveal variant="fadeUp" threshold={0} className="mx-auto w-full py-[108px]">
            <div className="flex h-[728px] items-center">
              {/* Content on the left - half width with white background */}
              <div className="flex h-[728px] w-1/2 shrink-0 flex-col items-start justify-center bg-white px-[80px] py-[137px]">
                {renderContent(false, null, true)}
              </div>
              {/* Image on the right - half width */}
              {section.imageUrl || (settings.images && settings.images.length > 0) ? (
                <div className="relative h-[728px] w-1/2 shrink-0 overflow-hidden">
                  <img
                    src={section.imageUrl || settings.images[0].url}
                    srcSet={getHeroImageSrcSet(section.imageUrl || settings.images[0].url)}
                    alt=""
                    className="size-full object-cover object-center"
                    loading="eager"
                  />
                </div>
              ) : (
                <div className="flex h-[728px] w-1/2 shrink-0 items-center justify-center bg-gray-200">
                  <span className="text-gray-500">No image selected</span>
                </div>
              )}
            </div>
          </ScrollReveal>
        </section>
      )
    }
    case "contained_image": {
      // Get image URL from section.imageUrl or settings.images array (same as other layouts)
      const containedImageUrl =
        section.imageUrl || (settings.images && settings.images.length > 0 ? settings.images[0].url : null)
      return (
        <section
          className={twMerge(
            "relative flex min-h-[700px] items-center justify-center p-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
          {/* Image container with rounded corners */}
          <div className="absolute inset-8 overflow-hidden rounded-lg">
            {containedImageUrl ? (
              <img
                src={containedImageUrl}
                srcSet={getHeroImageSrcSet(containedImageUrl)}
                alt=""
                className="absolute inset-0 size-full object-cover object-center"
                loading="eager"
                fetchpriority="high"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                <span className="text-gray-500">No image selected</span>
              </div>
            )}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
          </div>

          {/* Content overlaid on the image */}
          <ScrollReveal variant="fadeUp" threshold={0} className="relative z-10 mx-auto max-w-4xl px-5">
            {renderContent(true, "contained_image")}
          </ScrollReveal>
        </section>
      )
    }

    case "full_image":
    default: {
      // Get image URL from section.imageUrl or settings.images array (same as other layouts)
      const fullImageUrl =
        section.imageUrl || (settings.images && settings.images.length > 0 ? settings.images[0].url : null)
      return (
        <section
          className={twMerge(
            "relative flex min-h-[700px] w-full items-center justify-center",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: fullImageSectionBackground, aspectRatio: "16/9" }}>
          {fullImageUrl && (
            <img
              src={fullImageUrl}
              srcSet={getHeroImageSrcSet(fullImageUrl)}
              alt=""
              className="absolute inset-0 size-full object-cover object-center"
              loading="eager"
              fetchpriority="high"
            />
          )}
          <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />

          {/* Header superpuesto para full_image */}
          <div className="absolute inset-x-0 top-0 z-30">
            <HeaderSectionDesktop
              section={{ id: "hero-header", settings: { showPromoBanner: false } }}
              practice={practice}
              theme={theme}
              highlightedField={null}
              transparent={true}
              transparentBackgroundColor={"#000000"}
            />
          </div>

          <ScrollReveal variant="fadeUp" threshold={0} className="relative z-10 mx-auto max-w-4xl px-5">
            {renderContent(true, "full_image")}
          </ScrollReveal>

          {/* Shape Border - only rendered for shape border layouts */}
          {showShapeBorder && shapeType && (
            <div className="absolute inset-x-0 bottom-0 z-20">
              <ShapeBorder type={shapeType} color={theme?.colors?.bgLight || "#FFFFFF"} isMobile={false} />
            </div>
          )}
        </section>
      )
    }
  }
}

export default HeroSectionDesktop
